import { pb } from '@/libs/pocketbase'
import { tryCatch } from '@/utils/try-catch'
import type {
	TimeOffBalance,
	TimeOffPort,
	TimeOffRequest,
} from '../domain/timeoff'

type ListResult<T> = { items: T[] } | null

const toQuery = (params: Record<string, string | null | undefined>): string => {
	const sp = new URLSearchParams()
	for (const [k, v] of Object.entries(params)) {
		if (v !== null && v !== undefined && v !== '') sp.set(k, v)
	}
	const s = sp.toString()
	return s ? `?${s}` : ''
}

const orNullOn404 = async <T>(request: Promise<T>): Promise<T | null> => {
	const result = await tryCatch(request)
	if (!result.isSuccess) {
		if (result.error.code === 404) return null
		throw new Error(result.error.message)
	}
	return result.data
}

const pbTimeoff: TimeOffPort = {
	listRequests: async (filter) => {
		const raw = await pb.send<ListResult<TimeOffRequest> | TimeOffRequest[]>(
			`/api/hr/timeoff/requests${toQuery({
				q: filter.q,
				memberId: filter.memberId,
				memberIds: filter.memberIds?.join(',') ?? null,
				approvalStage: filter.approvalStage,
				type: filter.type,
				start: filter.start,
				end: filter.end,
			})}`,
			{ method: 'GET' },
		)
		return Array.isArray(raw) ? raw : (raw?.items ?? [])
	},

	getRequestById: (id) =>
		orNullOn404(pb.send<TimeOffRequest>(`/api/hr/timeoff/requests/${id}`, { method: 'GET' })),

	createRequest: (data) =>
		pb.send<TimeOffRequest>('/api/hr/timeoff/requests', {
			method: 'POST',
			body: JSON.stringify(data),
			headers: { 'Content-Type': 'application/json' },
		}),

	approveDept: (id, actorId, actorName, note) =>
		pb.send<TimeOffRequest>(`/api/hr/timeoff/requests/${id}/approve-dept`, {
			method: 'POST',
			body: JSON.stringify({ actorId, actorName, note }),
			headers: { 'Content-Type': 'application/json' },
		}),

	rejectDept: (id, actorId, actorName, note) =>
		pb.send<TimeOffRequest>(`/api/hr/timeoff/requests/${id}/reject-dept`, {
			method: 'POST',
			body: JSON.stringify({ actorId, actorName, note }),
			headers: { 'Content-Type': 'application/json' },
		}),

	approveHr: (id, actorId, actorName) =>
		pb.send<TimeOffRequest>(`/api/hr/timeoff/requests/${id}/approve-hr`, {
			method: 'POST',
			body: JSON.stringify({ actorId, actorName }),
			headers: { 'Content-Type': 'application/json' },
		}),

	rejectHr: (id, actorId, actorName, note) =>
		pb.send<TimeOffRequest>(`/api/hr/timeoff/requests/${id}/reject-hr`, {
			method: 'POST',
			body: JSON.stringify({ actorId, actorName, note }),
			headers: { 'Content-Type': 'application/json' },
		}),

	addTeamLeaderNote: (id, authorId, authorName, note) =>
		pb.send<TimeOffRequest>(`/api/hr/timeoff/requests/${id}/notes`, {
			method: 'POST',
			body: JSON.stringify({ authorId, authorName, note }),
			headers: { 'Content-Type': 'application/json' },
		}),

	listBalances: (memberId, memberIds) =>
		pb.send<TimeOffBalance[]>(
			`/api/hr/timeoff/balances${toQuery({
				memberId,
				memberIds: memberIds?.join(',') ?? null,
			})}`,
			{ method: 'GET' },
		),

	adjustBalance: (memberId, type, delta) =>
		pb.send<TimeOffBalance>('/api/hr/timeoff/balances/adjust', {
			method: 'POST',
			body: JSON.stringify({ memberId, type, delta }),
			headers: { 'Content-Type': 'application/json' },
		}),
}

export { pbTimeoff }
